import hyperdom from 'hyperdom'
import Search from './search.jsx'
import formatPrice from '../packages/ui-helpers/formatPrice.js'
import formatRelativePrice from '../packages/ui-helpers/formatRelativePrice.js'
import checkRoomAvailability from '../checkRoomAvailability.js'
import RoomOptions from '../roomOptions.jsx'
import baggageNotes from '../flights/baggageNotes.jsx'
import LocalDate from '@booking-services/localDate'
import transformPromisedFlightsResponse from './utils/transformPromisedFlightsResponse.js'
import pluralize from 'pluralize'
import formatDate from 'root-packages/dates/formatDate.js'
import formats from 'root-packages/dates/formats.js'
import * as gtmDataLayer from '../analytics/gtm/dataLayer.js'
import {Message, MessageTypes} from '../message.jsx'

class PromisedFlights {
  constructor (options) {
    options.quoteCriteria.promisedFlights = this
    this.config = options.config
    this.api = options.api
    this.showLoading = options.showLoading
    this.hideLoading = options.hideLoading
    this.modals = options.modals
    this.quoteCriteria = options.quoteCriteria
    this.departure = options.departure
    this.disableLoadingTimeout = options.disableLoadingTimeout
    this.isPromisedFlightsOpen = false
    this.exploreFlights = {
      getDepartDate () {
        return this.departureDate ? this.departureDate : 'Invalid Date'
      },
    }

    this.calculateFlightCost = (promisedFlight) => {
      if (!promisedFlight) {
        throw new Error('must specify promisedFlight')
      }

      const party = this.quoteCriteria.party
      const partyTotal = party.adults + party.children
      const exploreFlights = this.exploreFlights

      const departDates = {
        outbound: exploreFlights.departureDate,
        inbound: exploreFlights.returnDate,
      }

      return Object.assign({}, promisedFlight, {
        total: promisedFlight.price * partyTotal,
        legs: promisedFlight.legs.map(leg => {
          return Object.assign({}, leg, {departDate: departDates[leg.type]})
        }),
      })
    }

    this.promisedFlights = {
      type: 'explore',
      searchMode: 'loading',
      defaultDepartDate: this.departure.startDate,
      defaultReturnDate: this.departure.endDate,
      defaultAirports: {
        start: options.departure.startPlace.airport,
        end: options.departure.endPlace.airport,
      },
    }

    this.search = new Search(this, options.api)

    this.promisedFlights.select = (promisedFlight) => {
      this.promisedFlights.clearWarnings()
      this.selected = this.calculateFlightCost(promisedFlight)
      this.promisedFlights.searchMode = 'selected'
    },
    this.promisedFlights.recalculateFlightCost = () => {
      if (this.selected) {
        this.promisedFlights.select(this.selected)
      }
    },
    this.promisedFlights.remove = () => {
      delete this.selected
      this.renderExtraNightsWarning = undefined
      this.initDepartureDate = undefined
      this.initReturnDate = undefined
      this.promisedFlights.searchMode = 'criteria'
    },
    this.promisedFlights.clearWarnings = () => {
      delete this.addExtraNightsWarning
    },

    this.roomOptions = new RoomOptions({
      binding: {
        get: () => options.quoteCriteria.selectedRoomAllocation,
      },
      quoteCriteria: options.quoteCriteria,
    })
  }

  onadd () {
    gtmDataLayer.activateOptimize()
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  loadData () {
    if (!this._loadingData) {
      this.airports = []
      var homeCountry = this.config.country
      return this._loadingData = this.api.airports()
        .then(airports => {
          function formatAirport (airport) {
            return `${airport.name}, ${airport.city}, ${airport.code}`
          }

          this.airports = airports
          this.allAirports = airports.map(formatAirport)
          this.homeCountryAirports = airports
            .filter(airport => airport.country === homeCountry || this.config.promisedFlights.nonOriginCountryAllowed)
            .map(airport => formatAirport(airport))

          const arrivalAirport = this.airports.find(airport => airport.code === this.departure.startPlace.airport)
          if (arrivalAirport) {
            this.exploreFlights.arriveAirport = arrivalAirport
          }

          const returnAirport = this.airports.find(airport => airport.code === this.departure.endPlace.airport)
          if (returnAirport) {
            this.exploreFlights.returnAirport = returnAirport
          }

          if (this.promisedFlights.searchMode === 'loading') {
            this.promisedFlights.searchMode = 'criteria'
          }

        }, (error) => {
          this.hideLoading()
          throw error
        })
    } else {
      return this._loadingData
    }
  }


  offerPromisedFlights () {
    const config = this.config
    const departure = this.departure
    const hasBrochurePromisedFlights = (config.promisedFlights.brochuresWithPromisedFlights || []).includes(departure.brochureCode)

    return hasBrochurePromisedFlights && this.isDepartureEligibleForPromisedFlights()
  }

  isDepartureEligibleForPromisedFlights () {
    const config = this.config
    const departure = this.departure

    const startDate = departure.originDepartureDate ? departure.originDepartureDate : departure.startDate
    const nights = startDate.differenceInDays(departure.endDate)
    const hasMinimumRequiredNights = nights >= (config.promisedFlights.minimumTripDurationInNights || 0)

    const numberOfDaysToDeparture = new LocalDate().differenceInDays(startDate)
    const hasMinimumRequiredDaysBeforeDeparture = numberOfDaysToDeparture >= (config.promisedFlights.minimumLeastNumberOfDaysBeforeDeparture || 0)

    return hasMinimumRequiredNights && hasMinimumRequiredDaysBeforeDeparture
  }

  buildPreselectedFlightSearchString () {
    const exploreFlights = this.exploreFlights
    if (this.selected && exploreFlights.originAirportCode
      && exploreFlights.departureDate && exploreFlights.returnDate) {
      return `${exploreFlights.originAirportCode}_${exploreFlights.departureDate}_${exploreFlights.returnDate}`
    }
  }

  async preselectFlight (promisedFlightOptions) {
    if (!this.offerPromisedFlights() || this.selected || !promisedFlightOptions
      || !promisedFlightOptions.fromAirport || !promisedFlightOptions.departureDate || !promisedFlightOptions.returnDate) {
      return
    }

    const {start: tripStartAirport, end: tripEndAirport} = this.promisedFlights.defaultAirports
    const {fromAirport, departureDate, returnDate} = promisedFlightOptions
    Object.assign(this.exploreFlights, {originAirportCode: fromAirport, departureDate, returnDate})
    this.quoteCriteria.promisedFlights.hotelsBeforeSelectedNights = this.quoteCriteria.hotelsBefore.selectedNights.number
    this.quoteCriteria.promisedFlights.hotelsAfterSelectedNights =  this.quoteCriteria.hotelsAfter.selectedNights.number

    const preselectedFlightDetails = await this.api
      .promisedFlights({
        sellingCompanyCode: this.config.sellingCompanyCode,
        fromAirport,
        departureDate,
        destinationAirport: tripStartAirport,
        returningAirport: tripEndAirport,
      })
      .then(response => {
        const searchResults = transformPromisedFlightsResponse(response)
        return searchResults.find(flight => flight.cabin === 'economy')
      })
      .catch(() => undefined)

    if (preselectedFlightDetails) {
      this.promisedFlights.type = 'explore'
      this.promisedFlights.select(preselectedFlightDetails)
      this.togglePromisedFlights()
    }
  }

  openModalHandler () {
    return this.modals.show({
      render: () => this.renderModal({
        close: () => this.modals.close(),
      }),
      classNames: 'ttc-air-details',
    })
  }

  renderModal () {
    return (
      <div class="promised-flights__selling-company-modal">
        <div className='modal__header'>
          <h1>Contiki Air</h1>
          <i className='modal__exit' onclick={(ev) => {ev.preventDefault(); return this.modals.close()}}></i>
        </div>
        <div class='promised-flights__selling-company-modal__info'>
          <div class="promised-flights__selling-company-info__text">
            <p>The hassle free Contiki Air program leaves the complexity of planning air travel with the air experts. Flights schedules are sent no later than 45 days prior to departure which allows Contiki to offer the best deals and flights for our guests.</p>
            <h2>10 reasons to book air with Contiki</h2>
            <div class="promised-flights__selling-company-modal__raison">
              <ul>
                <li>One piece of checked baggage included</li>
                <li>Best available schedules</li>
                <li>Best price guaranteed with deposit only</li>
                <li>Full payment due 45 days prior to departure</li>
                <li>Insurance coverage for flights if purchased</li>
                <li>Mileage accrual on most carriers</li>
                <li>Monthly payments available</li>
                <li>Handling of air schedule changes</li>
                <li>Reduced air cancellation & change penalties</li>
                <li>Free pre-reserved seats on most carriers</li>
              </ul>
            </div>
            <h2>What will my flights be like?</h2>
            <p>We always do our best to provide the most direct route to your destination. In order to pass on the best overall value to our travelers, we cannot guarantee non-stop or direct flights and certain routings may not be available.</p>
            <p>Flights are booked with major airlines in economy class and include one piece of checked baggage per guest.  Additional services such as meals and seat requests are usually included as well. </p>
            <p>Most arrival and departure times at your destination range from 7:00am to 5:00pm local time. Transfers to and from the airport at your destination are not included but may be purchased separately.</p>
            <h2>What if I need to make changes to my Contiki Air?</h2>
            <p>Changes and/or cancellations can be made free of charge within 24 hours of booking Contiki Air. After 24 hours you are responsible for costs associated with changes you wish to make to your flights, including but not limited to name corrections and schedule preference requests. Fortunately, Contiki Air provides reduced air change and cancellation fees.</p>
            <h2>When will I receive my flight itinerary?</h2>
            <p>Your flight itinerary and e-ticket number will be posted in your My Travel Portal account no later than 45 days prior to the departure of your trip.</p>
            <h2>What if I want to choose my own flight schedule?</h2>
            <p>Contiki Air is the best value for money, but custom air requests can be made by contacting our reservations team at {this.config.phone.general}.</p>
            <h2>What if I need to arrive a day early or depart a day late? </h2>
            <p>When a pre or post accommodation is needed, you will be responsible for purchasing it with Contiki or independently. <strong>Please check the Important Trip Notes for start/end times to ensure that you arrange accommodations to cover all necessary pre/post nights.</strong></p>
            <h2>Do I have to travel on all segments of my flight itinerary?</h2>
            <p>Yes, you must travel on all segments of your flight itinerary. If you do not travel on a portion of your itinerary, the remaining flights will be cancelled by the airline. If this happens, you will be responsible for purchasing a new ticket or service fees charged by the airlines.</p>
            <h2>What happens if my flight is delayed or cancelled by the airline?</h2>
            <p> If you purchase your flights with Contiki, our Air Experts will assist you with schedule changes and cancellations caused by the airlines. Travel protection is highly recommended to offset costs associated with flight delays or cancellations caused by strikes, weather, mechanical issues, capacity, or any other reason.</p>
            <p>Flights purchased independently require you to work directly with the airline to get re-protected on a new itinerary as needed. Once your new flights are confirmed, you must then notify Contiki with your updated arrival time. Contiki is not responsible for costs associated with flight delays or cancellations, such as (but not limited to) hotels, meals, or additional airline charges.</p>
            <p>In the event an airline delay or cancellation causes you to miss time from your trip, you will not be reimbursed for missed portions of the trip, including but not limited to accommodations, meals, excursions or sightseeing tours. Purchasing travel insurance can help - to see full coverage of Contiki  Travel Protection <a target="_blank" href='https://affinitytravelcert.com/document/pdfs/ctpUS_Landing.html'>click here</a></p>
          </div>
        </div>
      </div>
    )
  }

  extraNightsWarning (nights, flightType, date) {
    if(nights > 0){
      return new Message({
        message: `As you have added ${pluralize('night', nights, true)} to your trip, your ${flightType} flight has been moved to the ${date}.`,
        type: MessageTypes.WARNING,
      })
    }
  }

  adjustPromisedFlightsDates (lastChange, nights) {

    if (this.addExtraNightsWarning) {

      this.tempDepartureDate =  (this.initDepartureDate && this.initDepartureDate) || this.exploreFlights.departureDate
      this.tempReturnDate = (this.initReturnDate && this.initReturnDate )|| this.exploreFlights.returnDate
      const hotelBeforeSelectedNights = (this.quoteCriteria.promisedFlights.hotelsBeforeSelectedNights && lastChange === 'pre') ?
        nights - this.quoteCriteria.promisedFlights.hotelsBeforeSelectedNights : this.quoteCriteria.hotelsBefore.selectedNights.number
      const hotelsAfterSelectedNights =  (this.quoteCriteria.promisedFlights.hotelsAfterSelectedNights && lastChange === 'post') ?
        nights - this.quoteCriteria.promisedFlights.hotelsAfterSelectedNights : this.quoteCriteria.hotelsAfter.selectedNights.number

      this.exploreFlights.departureDate = (this.quoteCriteria.promisedFlights.hotelsBeforeSelectedNights && lastChange === 'post') ?
        this.currentDepartureDate || this.tempDepartureDate : new LocalDate(this.tempDepartureDate).addDays(-hotelBeforeSelectedNights).toString()

      this.exploreFlights.returnDate = (this.quoteCriteria.promisedFlights.hotelsAfterSelectedNights &&lastChange === 'pre') ?
        this.currentReturnDate || this.tempReturnDate : new LocalDate(this.tempReturnDate).addDays(hotelsAfterSelectedNights).toString()

      if(lastChange === 'pre'){
        this.currentDepartureDate = this.exploreFlights.departureDate
        this.initDepartureDate =  new LocalDate(this.exploreFlights.departureDate).addDays(hotelBeforeSelectedNights).toString()
      }

      if(lastChange === 'post'){
        this.currentReturnDate = this.exploreFlights.returnDate
        this.initReturnDate =    new LocalDate(this.exploreFlights.returnDate).addDays(-hotelsAfterSelectedNights).toString()
      }


      this.search.criteria.search().then(()=>{
        const searchResults = this.promisedFlights.searchResults.length && this.promisedFlights.searchResults[0]
        this.promisedFlights.select(searchResults)
        const flightType =  lastChange === 'pre' ? 'outbound' : 'return'
        const adjuestedDate = lastChange === 'pre'? this.exploreFlights.departureDate : this.exploreFlights.returnDate
        const flightDate = formatDate(adjuestedDate, formats.humanDayMonthYear)
        this.renderExtraNightsWarning = this.extraNightsWarning(nights, flightType, flightDate)
      })

    }
  }

  recalculateFlightCost () {
    this.promisedFlights.recalculateFlightCost()
  }

  clear () {
    delete this.selected
    this.promisedFlights.searchMode = 'criteria'
  }

  party () {
    return this.quoteCriteria.party
  }

  formatPrice (price) {
    return formatPrice({
      currency: this.departure.prices.currency,
      amount: price,
    })
  }

  formatRelativePrice (price){
    return formatRelativePrice({
      currency: this.departure.prices.currency,
      amount: price,
      noPrefixSymbol: true,
    })
  }

  renderPromisedFlights () {
    const onRequestBaseOnRoomAvailability = checkRoomAvailability({
      rooms: this.roomOptions.binding.get().rooms,
      roomsOccupancy: this.departure.roomsOccupancy,
      roomAllocations: this.quoteCriteria.roomAllocations,
    })
    const departureOnRequest = this.departure.status === 'onRequest'
    let recommendationMessage
    if (!this.selected && (onRequestBaseOnRoomAvailability || departureOnRequest)) {
      recommendationMessage = (
        <div class="u-margin-bottom-medium">
          <div class='message-info -strong book-promised-flight-recommendation'>
            <h2>{this.config.promisedFlights.bookFlightRecommendationMessage}</h2>
          </div>
        </div>
      )
    }
    return (
      <div class="promised-flights">
        <div className='flights__header'>
          <h2>Flights to connect you with your trip</h2>
          {<span className='promised-flights__ribbon'>Included with your trip</span>}
        </div>
        <div className="flights__company-info o-accent-box">
          <div className='flights__company-info-header'>
            <i className='flights__company-info-icon -new-ui_plane'></i>
            <div className='flights__company-info-title'>Let the Contiki Air Experts plan your flights. With Contiki Air you get ...</div>
          </div>
          <ul className='flights__company-info-list'>
            <li>Best price guaranteed with deposit only</li>
            <li>Best available schedule</li>
            <li>Reduced air cancellation & change penalties</li>
          </ul>
          <div className='flights__company-info-bottom'>
            <label for={`flights__company-info-bottom-checkbox`} className='o-choice-item -fullWidth'>
              <input
                type='checkbox'
                checked={this.isPromisedFlightsOpen}
                onclick={(e)=>{e.preventDefault();this.togglePromisedFlights()}}
                className="o-choice-item__checkbox"
                id={`flights__company-info-bottom-checkbox`}/>
              <div className='o-choice-item__content'>Add flights to connect you with your trip</div>
              <a className='o-choice-item__button' href="#/" onclick={() => {event.preventDefault(); return this.openModalHandler()}}>Details</a>
            </label>
          </div>
        </div>
        <div className={["flights__search-container",{'show-expanded': this.isPromisedFlightsOpen}]}>
          <div className='flights__search-inner'>
            {recommendationMessage}
            {this.search}
          </div>
          <div className="flights__search-info-small u-margin-top-small">
            For your benefit and clarity, the total airfare shown includes airline imposed fuel surcharges and
            government taxes and fees. If you need more clarification please see our <a
              href={this.config.brandPages.termsAndConditions} target="_blank">terms & conditions</a>.
            {baggageNotes(this)}
          </div>
        </div>
      </div>
    )
  }

  render () {
    return <div>
      {this.renderPromisedFlights()}
    </div>
  }

  togglePromisedFlights () {
    this.isPromisedFlightsOpen ? this.promisedFlights.remove(): undefined
    this.isPromisedFlightsOpen = !this.isPromisedFlightsOpen
  }


}

export default PromisedFlights
